import type { AxiosResponse } from 'axios'

import { apiClient } from '@/api/client'

function filenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null
  const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch {
      // filename* percent-encoded inválido: cai para o filename simples abaixo
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i)
  return plain?.[1]?.trim() ?? null
}

export interface AuthenticatedDownloadOptions {
  method?: 'GET' | 'POST'
  data?: unknown
}

export interface AuthenticatedBlobResult {
  blob: Blob
  filename: string | null
}

export async function fetchAuthenticatedBlob(
  path: string,
  options?: AuthenticatedDownloadOptions,
): Promise<AuthenticatedBlobResult> {
  const requestConfig: Record<string, unknown> = {
    method: options?.method ?? 'GET',
    url: path,
    responseType: 'blob',
  }
  if (options?.data !== undefined) {
    requestConfig.data = options.data
  }

  const res = (await apiClient.request(requestConfig)) as AxiosResponse<Blob>

  const blob = res.data
  if (!(blob instanceof Blob)) {
    throw new Error('invalid_download_response')
  }

  return { blob, filename: filenameFromContentDisposition(res.headers?.['content-disposition']) }
}

export function saveBlobAsFile(blob: Blob, name: string): void {
  const blobUrl = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = name
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

export async function downloadAuthenticatedFile(
  path: string,
  fallbackName: string,
  options?: AuthenticatedDownloadOptions,
): Promise<void> {
  const { blob, filename } = await fetchAuthenticatedBlob(path, options)
  saveBlobAsFile(blob, filename ?? fallbackName)
}
